import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Settings2, ExternalLink, RefreshCw } from 'lucide-react';
import { privateGiveawayApi } from '../lib/giveaway-api';
import { GiveawayEntries } from './GiveawayEntries';
import { GiveawaySettings } from './GiveawaySettings';
import { GiveawayMonthPicker } from './GiveawayMonthPicker';
import '../giveaway.css';

export const GiveawayWorkspace = ({ admin = false }) => {
  const [brands, setBrands] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [slug, setSlug] = useState('');
  const [month, setMonth] = useState('');
  const [editing, setEditing] = useState(undefined);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const load = useCallback(async selected => {
    setLoading(true); setError('');
    try {
      const data = await privateGiveawayApi('/brands', admin);
      setBrands(data.brands); setAdvertisers(data.advertisers || []);
      setSlug(old => selected || old || data.brands[0]?.slug || '');
      setMonth(old => old || data.campaign_month);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [admin]);
  useEffect(() => { load(); }, [load]);
  const brand = brands.find(item => item.slug === slug);
  return <div className="giveaway-report ph-no-capture ph-mask" data-testid="giveaway-workspace">
    <header className="flex flex-wrap justify-between items-start gap-4 mb-6">
      <div><h2 className="text-lg font-bold">QR email giveaways</h2><p className="text-sm text-navy/60 mt-1">Email lists & monthly social attribution</p></div>
      <div className="flex gap-3"><button onClick={() => load()} title="Refresh brands" aria-label="Refresh brands" data-testid="giveaway-refresh"><RefreshCw size={18} /></button><button onClick={() => setEditing(null)} className="inline-flex gap-2 items-center text-sm text-navy underline" data-testid="giveaway-add-brand"><Plus size={16} />Add brand</button></div>
    </header>
    {error && <p className="text-red-700 text-sm mb-4" role="alert" data-testid="giveaway-workspace-error">{error} {admin && <Link to="/admin/bloom" data-testid="giveaway-admin-signin">Open Admin Bloom to sign in.</Link>}</p>}
    {loading ? <p data-testid="giveaway-workspace-loading">Loading brands…</p> : <>
      {!brands.length && !error && <p className="py-6 text-sm text-navy/60" data-testid="giveaway-no-brands">No email giveaway brands are assigned to this campaign yet.</p>}
      {brand && <>
        <div className="flex flex-wrap items-end gap-4 mb-5">
          <label className="text-xs font-semibold flex-1 min-w-[160px]">Brand<select value={slug} onChange={event => setSlug(event.target.value)} className="block w-full mt-1 p-2 border border-navy/20 rounded-md bg-white text-sm" data-testid="giveaway-brand-select">{brands.map(item => <option key={item.slug} value={item.slug}>{item.name}</option>)}</select></label>
          <GiveawayMonthPicker value={month} onChange={setMonth} />
          <button onClick={() => setEditing(brand)} className="inline-flex items-center gap-2 text-sm border border-navy/20 p-2 rounded-md" data-testid="giveaway-edit-brand"><Settings2 size={16} />Settings & entry method</button>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 items-center text-xs mb-5">
          <span data-testid="giveaway-active-mode">Active method: <strong>{brand.mode === 'email' ? 'QR email sign-up' : 'Instagram post & comments'}</strong></span>
          <span data-testid="giveaway-rules-status">Rules: {brand.rules_published ? 'Published' : 'Draft — sign-up closed'}</span>
          {!brand.advertiser_id && <strong className="text-amber-700" data-testid="giveaway-owner-missing">Assign an advertiser before launch</strong>}
          <a href={`/giveaway/${brand.slug}/rules`} target="_blank" rel="noopener noreferrer" className="inline-flex gap-1 underline" data-testid="giveaway-view-rules">Rules page <ExternalLink size={12} /></a>
        </div>
        <GiveawayEntries key={brand.slug} brand={brand} month={month} admin={admin} />
      </>}
    </>}
    {editing !== undefined && <GiveawaySettings brand={editing} admin={admin} advertisers={advertisers} onClose={() => setEditing(undefined)} onSaved={selected => { setEditing(undefined); load(selected); }} />}
  </div>;
};